import { useEffect } from "react";
import { useReviewStats } from "@/hooks/useReviewStats";

export const SITE_URL = "https://www.nettoyage-expert.ch";
const LOGO_URL = `${SITE_URL}/logo-black.png`;
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/social/og-share-main.jpg`;

export function useLocalBusinessSchema() {
  const { count, average_rating } = useReviewStats();
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${SITE_URL}/#business`,
    "name": "Nettoyage Expert",
    "url": SITE_URL,
    "telephone": "+41245391033",
    "email": "info@nettoyage-expert.ch",
    "image": `${SITE_URL}/logo-black.png`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Rue Jean-André Venel 4",
      "addressLocality": "Yverdon-les-Bains",
      "postalCode": "1400",
      "addressRegion": "VD",
      "addressCountry": "CH"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": String(average_rating),
      "reviewCount": String(count),
      "bestRating": "5"
    },
    "areaServed": [
      { "@type": "City", "name": "Lausanne" },
      { "@type": "City", "name": "Genève" },
      { "@type": "City", "name": "Yverdon-les-Bains" },
      { "@type": "City", "name": "Montreux" },
      { "@type": "City", "name": "Nyon" },
      { "@type": "City", "name": "Fribourg" },
      { "@type": "City", "name": "Neuchâtel" },
      { "@type": "City", "name": "Sion" }
    ],
    "priceRange": "CHF"
  };
}

// Keep for backward compatibility — static version with default 36
export const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#business`,
  "name": "Nettoyage Expert",
  "url": SITE_URL,
  "telephone": "+41245391033",
  "email": "info@nettoyage-expert.ch",
  "image": `${SITE_URL}/logo-black.png`,
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Rue Jean-André Venel 4",
    "addressLocality": "Yverdon-les-Bains",
    "postalCode": "1400",
    "addressRegion": "VD",
    "addressCountry": "CH"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "reviewCount": "36",
    "bestRating": "5"
  },
  "areaServed": [
    { "@type": "City", "name": "Lausanne" },
    { "@type": "City", "name": "Genève" },
    { "@type": "City", "name": "Yverdon-les-Bains" },
    { "@type": "City", "name": "Montreux" },
    { "@type": "City", "name": "Nyon" },
    { "@type": "City", "name": "Fribourg" },
    { "@type": "City", "name": "Neuchâtel" },
    { "@type": "City", "name": "Sion" }
  ],
  "priceRange": "CHF"
};

function setMeta(attr, attrValue, content) {
  let el = document.querySelector(`meta[${attr}="${attrValue}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function SeoHead({ title, description, path = "/", type = "website", ogImage, noindex = false, breadcrumbs = [], schemas = [] }) {
  const canonical = `${SITE_URL}${path}`;
  const cleanTitle = title.replace(/\s*\|[^|]*Nettoyage Expert[^|]*$/i, "") + " | Nettoyage Expert";
  const finalOgImage = ogImage ? (ogImage.startsWith("http") ? ogImage : `${SITE_URL}${ogImage}`) : DEFAULT_OG_IMAGE;
  const robotsContent = noindex ? "noindex, follow" : "index, follow";

  useEffect(() => {
    document.title = cleanTitle;
    setMeta("name", "description", description);
    setMeta("name", "robots", robotsContent);
    if (!noindex) {
      setLink("canonical", canonical);
    } else {
      // Remove canonical for noindex pages
      const el = document.querySelector('link[rel="canonical"]');
      if (el) el.remove();
    }
    setMeta("property", "og:title", cleanTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", finalOgImage);
    setMeta("property", "og:image:width", "1200");
    setMeta("property", "og:image:height", "630");
    setMeta("property", "og:site_name", "Nettoyage Expert");
    setMeta("property", "og:locale", "fr_CH");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", cleanTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", finalOgImage);
  }, [cleanTitle, description, canonical, type, finalOgImage, noindex, robotsContent]);

  const allSchemas = [];

  if (breadcrumbs.length > 0) {
    allSchemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((b, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": b.name,
        ...(b.url ? { "item": `${SITE_URL}${b.url}` } : {})
      }))
    });
  }

  allSchemas.push(...schemas);

  if (allSchemas.length === 0) return null;

  return (
    <>
      {allSchemas.map((schema, i) => (
        <script
          key={`ld-${i}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}
